import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { TableHead } from "./table";
import { cn } from "@/lib/utils";
import type { SortDirection } from "@/hooks/useSortableData";

type SortableTableHeadProps = Omit<
  React.ThHTMLAttributes<HTMLTableCellElement>,
  "onClick" | "children" | "className" | "style"
> & {
  children: React.ReactNode;
  /** Current sort direction for this column, or null if not sorted */
  direction: SortDirection | null;
  /** Called when header is clicked */
  onSort: () => void;
  /** Additional className */
  className?: string;
  /** Inline styles (e.g. for dynamic column widths) */
  style?: React.CSSProperties;
  /** Disable sorting for this column */
  disabled?: boolean;
};

/**
 * Sortable table header cell
 *
 * Shows sort indicator and handles click to toggle sort.
 * Works with useSortableData hook.
 *
 * @example
 * ```tsx
 * <SortableTableHead
 *   direction={getSortDirection("name")}
 *   onSort={() => requestSort("name")}
 * >
 *   Name
 * </SortableTableHead>
 * ```
 */
export function SortableTableHead({
  children,
  direction,
  onSort,
  className,
  style,
  disabled = false,
  ...rest
}: SortableTableHeadProps) {
  const Icon =
    direction === "asc"
      ? ArrowUp
      : direction === "desc"
        ? ArrowDown
        : ArrowUpDown;

  return (
    <TableHead
      // Native <th> attributes (rowSpan, colSpan, scope, aria-*) pass through.
      // They used to be dropped, so PivotTreeTable's `rowSpan: 2` on the tree
      // header never reached the DOM and every pivot sort header sat one
      // column left of its data.
      {...rest}
      className={cn(
        "select-none",
        !disabled && "cursor-pointer hover:bg-slate-50 transition-colors",
        className,
      )}
      style={style}
      onClick={disabled ? undefined : onSort}
    >
      <div className="flex items-center gap-1">
        <span>{children}</span>
        {!disabled && (
          <Icon
            className={cn(
              "h-4 w-4 flex-shrink-0",
              direction ? "text-slate-700" : "text-slate-400",
            )}
          />
        )}
      </div>
    </TableHead>
  );
}
